import React from "react";
import { CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, endpoints } from "@/lib/api";
import { DASHBOARD } from "@/constants/testIds";

export const CauseSelection = ({ causes = [], selected, onChange }) => {
    const handlePick = async (id) => {
        try {
            await api.post(endpoints.setUserCause(), {
                user_id: "demo-user",
                selected_cause: id,
            });
            onChange?.(id);
            toast.success("Impact focus updated", {
                description: `Your membership now emphasizes ${
                    causes.find((c) => c.id === id)?.name
                }.`,
            });
        } catch (e) {
            toast.error("Could not update your cause. Please try again.");
        }
    };

    return (
        <section
            data-testid={DASHBOARD.causes}
            className="rounded-lg border border-[#1f1f22] bg-[#080808] p-6 sm:p-8 lg:p-10"
        >
            <div className="mb-6 flex flex-col gap-3 sm:mb-8 md:flex-row md:items-end md:justify-between">
                <div>
                    <span className="eviq-chip">My Impact · Personalization</span>
                    <h2 className="mt-4 font-editorial text-2xl leading-tight text-white sm:text-3xl md:text-4xl">
                        Choose the cause your membership emphasizes.
                    </h2>
                </div>
                <p className="max-w-sm text-sm text-zinc-500">
                    Every EVIQ member contributes the same. You decide where the emphasis goes.
                </p>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {causes.map((c) => {
                    const active = selected === c.id;
                    return (
                        <button
                            key={c.id}
                            type="button"
                            data-testid={DASHBOARD.causeCard(c.id)}
                            onClick={() => handlePick(c.id)}
                            className={`group relative h-[280px] overflow-hidden rounded-md border text-left transition-colors duration-300 ${
                                active
                                    ? "border-[#a3e635] eviq-glow-green"
                                    : "border-[#1f1f22] hover:border-[#3a3a3f]"
                            }`}
                        >
                            <img
                                src={c.image}
                                alt={c.name}
                                loading="lazy"
                                className="absolute inset-0 h-full w-full object-cover opacity-60 transition-transform duration-700 group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/60 to-black/30" />

                            {active && (
                                <div className="absolute right-4 top-4 flex items-center gap-1 rounded-full bg-[#a3e635] px-3 py-1 font-mono-metric text-[10px] uppercase tracking-[0.16em] text-black">
                                    <CheckCircle size={14} weight="fill" />
                                    Selected
                                </div>
                            )}

                            <div className="absolute inset-x-0 bottom-0 p-5">
                                <h3 className="font-editorial text-2xl text-white">{c.name}</h3>
                                <p className="mt-1 text-sm text-zinc-300">{c.tagline}</p>
                                <p className="mt-3 font-mono-metric text-[10px] uppercase tracking-[0.2em] text-zinc-400">
                                    Partners · {c.partners.slice(0, 2).join(", ")}
                                </p>
                            </div>
                        </button>
                    );
                })}
            </div>
        </section>
    );
};

export default CauseSelection;
